export type IngestLane = 'live' | 'broken' | 'pending_auth' | 'retired';

export const DEFAULT_INGEST_LANES: IngestLane[] = ['live', 'broken'];

/** Same 24h live window as orchestrator-api `api-ingest-display-status`. */
export const INGEST_LIVE_WINDOW_MS = 24 * 60 * 60 * 1000;

const PENDING_STATUSES = new Set([
  'pending_authentication',
  'pending_oauth',
  'pending_configuration',
  'pending',
  'awaiting_credentials',
  'awaiting_oauth',
  'draft',
]);
const RETIRED_STATUSES = new Set([
  'retired',
  'disabled',
  'inactive',
  'archived',
]);

export const INGEST_LANE_LABEL: Record<IngestLane, string> = {
  live: 'Live',
  broken: 'Broken',
  pending_auth: 'Pending',
  retired: 'Retired',
};

export type IngestRuntimeFacts = {
  latest_ingest_at?: string | null;
  last_success_at?: string | null;
  last_attempt_at?: string | null;
  last_run_at?: string | null;
  last_error?: string | null;
  failed_count_window?: number;
  health?: string | null;
  lane?: IngestLane | string | null;
  lane_reason?: string | null;
  registry_status?: string | null;
  pipeline_count?: number;
  credential_presence?: { missing_reason?: string | null } | null;
};

function parseIngestTime(value?: string | null): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * A stored last_error is current only when nothing later succeeded.
 * Dataflow/Observe already see pipeline.last_error=null after success;
 * Ingest workspace runtime historically kept the last failed history row.
 */
export function currentIngestError(runtime?: IngestRuntimeFacts | null): string | null {
  const health = String(runtime?.health || '').toLowerCase();
  if (health === 'healthy' || health === 'ok') return null;
  const lastError = String(runtime?.last_error || '').trim();
  if (!lastError) return null;
  const successMs = parseIngestTime(runtime?.latest_ingest_at || runtime?.last_success_at);
  const attemptMs = parseIngestTime(runtime?.last_attempt_at || runtime?.last_run_at);
  if (successMs != null && (attemptMs == null || successMs >= attemptMs)) return null;
  return lastError;
}

export function deriveHonestIngestLane(
  api?: { status?: string | null; runtime?: IngestRuntimeFacts | null } | null,
  now = Date.now(),
): { lane: IngestLane; reason: string } {
  const registryStatus = String(api?.status || api?.runtime?.registry_status || '').toLowerCase();
  if (PENDING_STATUSES.has(registryStatus)) {
    return { lane: 'pending_auth', reason: `registry_status:${registryStatus}` };
  }
  if (RETIRED_STATUSES.has(registryStatus)) {
    return { lane: 'retired', reason: `registry_status:${registryStatus}` };
  }

  const runtime = api?.runtime;
  const successMs = parseIngestTime(runtime?.latest_ingest_at || runtime?.last_success_at);
  const currentError = currentIngestError(runtime);

  if (!successMs) {
    if (!currentError && runtime?.lane === 'live') {
      return { lane: 'live', reason: 'reported_live_no_current_error' };
    }
    return {
      lane: 'broken',
      reason: currentError
        ? `never_ingested_with_error:${currentError}`
        : Number(runtime?.failed_count_window || 0) > 0
          ? 'never_ingested_with_failures'
          : 'never_ingested',
    };
  }

  if (currentError) {
    return { lane: 'broken', reason: `last_error:${currentError}` };
  }

  if ((now - successMs) <= INGEST_LIVE_WINDOW_MS) {
    return { lane: 'live', reason: 'success_in_24h' };
  }

  return { lane: 'broken', reason: 'stale_success' };
}

export function applyHonestIngestRuntime<T extends { status?: string | null; runtime?: IngestRuntimeFacts }>(
  api: T,
  now = Date.now(),
): T {
  const derived = deriveHonestIngestLane(api, now);
  return {
    ...api,
    runtime: {
      ...api.runtime,
      lane: derived.lane,
      last_error: currentIngestError(api.runtime),
      lane_reason: derived.reason,
    },
  };
}

export function countHonestIngestLanes(
  apis: Array<{ status?: string | null; runtime?: IngestRuntimeFacts | null }>,
  now = Date.now(),
): Record<IngestLane | 'all', number> {
  const counts: Record<IngestLane | 'all', number> = {
    all: apis.length,
    live: 0,
    broken: 0,
    pending_auth: 0,
    retired: 0,
  };
  for (const api of apis) {
    counts[deriveHonestIngestLane(api, now).lane] += 1;
  }
  return counts;
}

export function presentPipelineLastError(pipeline?: {
  last_error?: string | null;
  last_success_at?: string | null;
  last_success?: string | null;
  last_run_at?: string | null;
  health?: string | null;
} | null): string {
  const current = currentIngestError({
    last_error: pipeline?.last_error,
    latest_ingest_at: pipeline?.last_success_at || pipeline?.last_success,
    last_attempt_at: pipeline?.last_run_at,
    health: pipeline?.health,
  });
  if (current) return current;
  if (!pipeline?.last_success_at && !pipeline?.last_success) {
    return pipeline?.last_run_at ? 'scheduled_never_succeeded' : 'scheduled_never_run';
  }
  return '—';
}

export function isIngestLane(value: string | null | undefined): value is IngestLane {
  return value === 'live' || value === 'broken' || value === 'pending_auth' || value === 'retired';
}

export function matchesIngestLanes(
  lane: string | null | undefined,
  selected: Array<IngestLane | 'all'>
): boolean {
  if (selected.includes('all')) return true;
  if (!isIngestLane(lane)) return selected.includes('broken');
  return selected.includes(lane);
}

export function laneChipColor(lane: IngestLane): 'success' | 'error' | 'warning' | 'default' {
  if (lane === 'live') return 'success';
  if (lane === 'broken') return 'error';
  if (lane === 'pending_auth') return 'warning';
  return 'default';
}

export function presentPipelineKpi(runtime?: {
  pipeline_count?: number;
  enabled_pipeline_count?: number;
  published_count_window?: number;
  records_24h?: number;
  uses_pipeline_objects?: boolean;
}): { value: string; caption: string } {
  const pipelines = Number(runtime?.pipeline_count || 0);
  const enabled = Number(runtime?.enabled_pipeline_count || 0);
  const events = Number(runtime?.published_count_window || runtime?.records_24h || 0);
  if (pipelines === 0 && events > 0) {
    return {
      value: `${events} runs`,
      caption: 'Ingest history — this API has no scheduled pipeline objects',
    };
  }
  if (pipelines === 0) {
    return {
      value: 'None',
      caption: 'No scheduled pipeline objects. Feed runs, if any, live in ingest history (same store as Data Flow).',
    };
  }
  return {
    value: `${enabled}/${pipelines}`,
    caption: 'Scheduled pipeline objects (api_ingest_pipelines)',
  };
}

export function presentAuthChip(api?: {
  auth_type?: string | null;
  status?: string | null;
  runtime?: {
    lane?: IngestLane | string | null;
    last_error?: string | null;
    credential_presence?: { missing_reason?: string | null } | null;
  };
}): { label: string; color: 'success' | 'error' | 'warning' | 'default' } | null {
  const authType = String(api?.auth_type || '').toLowerCase();
  const lastError = currentIngestError(api?.runtime)
    || String(api?.runtime?.credential_presence?.missing_reason || '');
  const honestLane = deriveHonestIngestLane(api).lane;
  if (authType === 'api_key') {
    if (lastError === 'missing_api_key') {
      return { label: 'Missing API key', color: 'error' };
    }
    return null;
  }
  if (authType !== 'oauth2') return null;
  const status = String(api?.status || '').toLowerCase();
  if (status === 'pending_authentication' || status === 'pending_oauth') {
    return { label: 'Needs OAuth', color: 'warning' };
  }
  if (honestLane === 'live') {
    return { label: 'OAuth Connected', color: 'success' };
  }
  if (lastError || honestLane === 'broken') {
    return { label: 'OAuth recorded — ingest broken', color: 'error' };
  }
  if (status === 'active') {
    return { label: 'OAuth recorded — never ingested', color: 'warning' };
  }
  return { label: 'Needs OAuth', color: 'warning' };
}

export function presentVisibleLastError(runtime?: {
  last_error?: string | null;
  latest_ingest_at?: string | null;
  last_attempt_at?: string | null;
  pipeline_count?: number;
  failed_count_window?: number;
  lane?: string | null;
  registry_status?: string | null;
} | null, registryStatus?: string | null): string {
  const currentError = currentIngestError(runtime);
  if (currentError) return currentError;
  if (runtime?.failed_count_window && !runtime?.latest_ingest_at) {
    return `${runtime.failed_count_window} failures`;
  }
  const registry = String(runtime?.registry_status || registryStatus || '').toLowerCase();
  if (
    runtime?.lane === 'pending_auth'
    || registry === 'pending_authentication'
    || registry === 'pending_oauth'
  ) {
    return 'None';
  }
  if (!runtime?.latest_ingest_at && Number(runtime?.pipeline_count || 0) > 0) {
    return runtime?.last_attempt_at ? 'scheduled_never_succeeded' : 'scheduled_never_run';
  }
  if (!runtime?.latest_ingest_at && Number(runtime?.pipeline_count || 0) === 0) {
    return 'never_scheduled';
  }
  return 'None';
}

export function leftoverOwnerChipLabel(owner: string | null | undefined): string | null {
  const id = String(owner || '').trim();
  if (!id) return null;
  return `leftover owner: ${id}`;
}

export function presentVolumeOrTimeout(opts: {
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string | null;
  total?: number | null;
  rate?: number | null;
}): { count: string | number; subtitle: string; timedOut: boolean } {
  if (opts.isLoading) {
    return { count: '…', subtitle: 'Loading 1h volume', timedOut: false };
  }
  const message = String(opts.errorMessage || '').toLowerCase();
  const timedOut = Boolean(
    opts.isError && (message.includes('timeout') || message.includes('timed out') || message.includes('504'))
  );
  if (opts.isError || opts.total == null) {
    return {
      count: '—',
      subtitle: timedOut
        ? '1h volume timed out — not zero traffic'
        : '1h volume unavailable — this view, not no traffic',
      timedOut,
    };
  }
  const rate = typeof opts.rate === 'number' && Number.isFinite(opts.rate) ? opts.rate : null;
  return {
    count: opts.total,
    subtitle: rate == null ? 'msgs/min unavailable' : `${rate} msgs/min`,
    timedOut: false,
  };
}
